/**
 * Recursively converts `undefined` values to `null` so cleared fields are
 * explicitly sent in the request body instead of being dropped by
 * `JSON.stringify` (which omits `undefined`). This ensures the backend
 * overwrites/clears previously saved data when a user empties a field.
 *
 * Arrays are preserved as arrays (their elements are transformed). Plain
 * objects are transformed key-by-key. The input type is preserved for callers.
 */
export function nullifyUndefinedDeep<T>(value: T): T {
  return transform(value) as T;
}

function transform(value: unknown): unknown {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map((item) => transform(item));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      out[key] = transform((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
